import type { PhotoSwipeItemData, PhotoSwipeOptions } from '../libs/photoswipe/js/core/base'
import PhotoSwipeLightbox from '../libs/photoswipe/js/lightbox/lightbox'
import PhotoSwipeDynamicCaption from 'photoswipe-dynamic-caption-plugin'
import '../libs/photoswipe/photoswipe.pcss'
import 'photoswipe-dynamic-caption-plugin/photoswipe-dynamic-caption-plugin.css'
import PhotoSwipe from '../libs/photoswipe/js/photoswipe'
import { decode } from 'html-entities'
import SGPlugins from '../common/SGPlugins'
import * as React from 'preact'
import VideoPlayer from '../components/VideoPlayer'

declare global {
  interface HTMLElement {
    galleryAttached: boolean
    swiper: any
  }
}

const galleries = []
let videoCssAppended = false

const commonConfig: PhotoSwipeOptions = {
  bgClickAction: 'close',
  preloadFirstSlide: true,
  paddingFn: (viewportSize) => {
    if (viewportSize.x > 600) {
      return { top: 30, bottom: 30, left: 70, right: 70 }
    }

    return { top: 0, right: 0, bottom: 0, left: 0 }
  },
  pswpModule: PhotoSwipe
}

const appendVideoCss = () => {
  if (videoCssAppended) {
    return
  }

  const style = document.createElement('style')
  style.innerHTML = `
    :root {
      
    }
  
    .pswp__video-container {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
    }
    
    .pswp__video-container iframe,
    .pswp__video-container lite-youtube,
    .pswp__video-container > div {
      width: calc(100vw - 200px);
      max-height: 90vh;
      aspect-ratio: 16/9;
    }
    
    .pswp__video-container lite-youtube:has(iframe) {
      aspect-ratio: unset;
    }
    
    .pswp__dynamic-caption--below {
      background: var(--article-insert-bg);
      margin-top: 8px;
      padding: 8px;
      border-radius: 8px;
      text-align: center;
      width: auto !important;
      left: 0 !important;
      margin-left: 50vw;
      transform: translateX(-50%);
      color: var(--article-caption-fg);
    }
    
    @media (max-width: 768px) {
      .pswp__video-container iframe,
      .pswp__video-container lite-youtube,
      .pswp__video-container > div {
        width: 100%;
      }
    }
  `
  document.head.appendChild(style)

  videoCssAppended = true
}

// noinspection JSUnusedGlobalSymbols
export default (elements: NodeListOf<HTMLElement>) => {
  appendVideoCss()
  elements.forEach((element: HTMLElement) => {
    let galleryName: string;
    let options: PhotoSwipeOptions = {
      // @ts-ignore
      gallery: element
    }

    if (element.galleryAttached) {
      return
    }
    element.galleryAttached = true

    let swiperIntegration = false

    if (element.hasAttribute('data-lightbox-rel')) {
      galleryName = element.getAttribute('data-lightbox-rel')

      if (element.hasAttribute('data-swiper-config')) {
        swiperIntegration = true
      }

      options.children = 'a[data-lightbox-children],div[data-lightbox-children][data-lightbox-type="video"]'
      options.preloadFirstSlide = false
    } else {
      options.closeOnScroll = true
    }

    options = {
      ...options,
      ...commonConfig
    }

    const gallery = new PhotoSwipeLightbox(options)

    gallery.addFilter('itemData', (itemData: PhotoSwipeItemData) => {
      const element = itemData.element
      if (element.hasAttribute('data-lightbox-type')) {
        itemData.type = element.getAttribute('data-lightbox-type')
      }
      if (element.hasAttribute('data-lightbox-provider')) {
        // @ts-ignore
        itemData.provider = element.getAttribute('data-lightbox-provider')
      }
      return itemData
    })

    const popStateHandler = (event: PopStateEvent) => {
      if (event.state === null || !event.state?.lightbox) {
        gallery.pswp.close()
      }
    }

    gallery.on('beforeOpen', () => {
      if (window.location.hash !== '') {
        window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
      }
      window.history.pushState({ lightbox: true }, '')
      window.addEventListener('popstate', popStateHandler)
    })

    gallery.on('close', () => {
      if (window.history.state?.lightbox) {
        window.history.back()
      }
      window.removeEventListener('popstate', popStateHandler)
    })

    gallery.on('contentLoad', (e: Event & {
      content: {
        type: 'video' | 'image',
        videoId?: string,
        start?: number,
        src: string,
        data: {
          provider: string,
          src: string,
          element: HTMLElement
        },
        element?: HTMLElement
      }
    }) => {
      const { content } = e

      if (content.type !== 'video') {
        return
      }
      e.preventDefault()

      content.element = document.createElement('div')
      content.element.className = 'pswp__video-container'

      switch (content.data.provider) {
        case 'youtube':
          content.videoId = /(?:https?:)?(?:\/\/)?(?:[0-9A-Z-]+\.)?(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/\S*?[^\w\s-])((?!videoseries)[\w-]{11})(?=[^\w-]|$)(?![?=&+%\w.-]*(?:['"][^<>]*>|<\/a>))[?=&+%\w.-]*/.exec(content.data.src)[1]
          let searchParams = new URL(content.data.src).searchParams
          content.start = searchParams.has('t') ? parseInt(searchParams.get('t')) : 0
          const element = document.createElement('lite-youtube')
          element.setAttribute('videoid', content.videoId)
          element.setAttribute('params', `enablejsapi=1&start=${content.start}`)
          content.element.appendChild(element)
          SGPlugins.callPlugin('lite-youtube-embed', content.element)
          return
        case 'vimeo':
          content.videoId = /https:\/\/(?:www.)?vimeo.com\/(channels\/\d+\/)?([0-9]{9})(?:#t=(\d+)s)?/i.exec(content.data.src)[2]
          content.src = `https://player.vimeo.com/video/${content.videoId}`
          break
        case 'own':
          const video = content.data.element.querySelector('video')
          const files = content.data.element.getAttribute('data-lightbox-files').split(',')
          const title = content.data.element.getAttribute('data-lightbox-title')
          const container = document.createElement('div')
          content.element.appendChild(container)
          React.render(React.createElement(VideoPlayer, {
            poster: video.getAttribute('poster'),
            files,
            title
          }), container)
          return
      }

      const iframe = document.createElement('iframe')
      iframe.src = content.src
      iframe.setAttribute('frame-border', '0')
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
      iframe.allowFullscreen = true
      content.element.appendChild(iframe)
    })

    gallery.on('uiRegister', () => {
      gallery.pswp.ui.registerElement({
        name: 'test-button',
        ariaLabel: 'Скачать',
        order: 9,
        isButton: true,
        html: {
          isCustomSVG: true,
          outlineID: 'pswp__icn-download',
          inner: '<path d="M23.3 19.5v3.8c0 .7-.5 1.2-1.1 1.2H9.8c-.6 0-1.1-.5-1.1-1.2v-3.8a1.2 1.2 0 0 1 2.3 0V22h10v-2.6a1.2 1.2 0 0 1 2.3 0zm-8.1 1a1.2 1.2 0 0 0 1.6 0l3.5-3.5c.5-.5.5-1.2 0-1.7-.4-.4-1.2-.4-1.6 0l-1.5 1.5V8.7a1.2 1.2 0 1 0-2.4 0v8.1l-1.5-1.5a1.2 1.2 0 1 0-1.6 1.7z"/>'
        },
        onInit: (el, pswp) => {
          pswp.on('change', () => {
            const hide = pswp.currSlide?.data?.type === 'video'
            el.style.display = hide ? 'none' : 'block'
          });
        },
        onClick: async (_event, _el, pswp) => {
          const image = await fetch(pswp.currSlide?.data?.src + `?no-cache=1`)
          const imageBlog = await image.blob()
          const imageURL = URL.createObjectURL(imageBlog)

          const link = document.createElement('a')
          link.href = imageURL
          link.download = pswp.currSlide?.data?.src?.split('/').pop()
          link.download = link.download.split('.').slice(0, -1).join('_')
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      });
    });

    gallery.on('change', () => {
      window.dispatchEvent(new Event('stopAllPlayers'))
    })

    if (swiperIntegration) {
      gallery.on('change', () => {
        if (!element.swiper || typeof element.swiper.slideTo !== 'function') {
          return
        }
        element.swiper.slideTo(gallery.pswp.currIndex, 300, true)
      })

      gallery.addFilter('clickedIndex', (index: number, event: Event & {
        target: HTMLElement
      }) => {
        if (event.target.classList.contains('swiper-slide-active') || event.target.closest('.swiper-slide-active')) {
          return index
        }
        if (element.swiper) {
          if (event.target === element.swiper.navigation.nextEl
            || event.target === element.swiper.navigation.prevEl
            || event.target.closest('.swiper-button-next')
            || event.target.closest('.swiper-button-prev')
          ) {
            return;
          }
        }
        event.preventDefault()
        if (element.swiper && element.swiper.slideTo) {
          element.swiper.slideTo(index, 300, true)
        }
        return;
      })
    }

    // noinspection JSUnusedGlobalSymbols
    new PhotoSwipeDynamicCaption(gallery, {
      type: 'below',
      captionContent: (slide) => {
        const text = slide.data?.element?.querySelector('img')?.getAttribute('alt')
        return decode(text || '')
      }
    });

    gallery.init()

    if (galleryName) {
      galleries.push(galleryName)
    }
  })
}
